import { useState, useCallback, useMemo } from 'react';
import { useDebounce } from './useDebounce';
import type { DietitianProfile, FilterState, UseDietitianFilteringReturn } from '../types/referral';
import { DietitianUtils } from '../utils/DietitianUtils';

/**
 * Hook for managing dietitian filtering with debounced search
 * Provides efficient filtering and search capabilities for the dietitian selection step
 */
export const useDietitianFiltering = (
  dietitians: DietitianProfile[],
  initialFilters: FilterState = { search: '', specialty: '', location: '' }
): UseDietitianFilteringReturn => {
  // Ensure dietitians is always an array
  const safeDietitians = dietitians || [];

  // Ensure initialFilters is valid
  const safeInitialFilters = initialFilters || { search: '', specialty: '', location: '' };

  const [filters, setFilters] = useState<FilterState>(safeInitialFilters);

  // Debounce search input for better performance
  const { debouncedValue: debouncedSearch } = useDebounce(filters?.search || '', 300);

  // Apply filters to dietitian list
  const filteredDietitians = useMemo(() => {
    return DietitianUtils.filterDietitians(
      safeDietitians,
      debouncedSearch,
      filters?.specialty || '',
      filters?.location || ''
    );
  }, [safeDietitians, debouncedSearch, filters?.specialty, filters?.location]);

  // Get result count
  const resultCount = filteredDietitians.length;

  // Check if any filters are active
  const isFiltered = useMemo(() => {
    return !!(filters?.search || filters?.specialty || filters?.location);
  }, [filters]);

  // Apply filters function
  const applyFilters = useCallback((newFilters: FilterState) => {
    if (newFilters) {
      setFilters(newFilters);
    }
  }, []);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilters({ search: '', specialty: '', location: '' });
  }, []);

  return {
    filteredDietitians,
    resultCount,
    applyFilters,
    clearFilters,
    isFiltered,
  };
};